export const LOGISTICS_IMPORT_BUSINESS_TYPE = "LOGISTICS_IMPORT";

export interface LogisticsImportTaskLike {
    id?: string | number;
    taskType?: string;
    businessType?: string;
    businessId?: string;
    businessScene?: string;
    status?: number;
}

export interface CompletedLogisticsImportTaskOptions<T extends LogisticsImportTaskLike> {
    tasks: T[];
    importTaskType: string;
    completedStatus: number;
    currentBusinessId: string;
    currentBusinessScene: string;
    pendingTaskId?: string | number;
    handledTaskIds: Set<string | number>;
}

const normalizeTaskId = (id?: string | number) => {
    return id === undefined ? undefined : String(id);
};

const hasHandledTask = (handledTaskIds: Set<string | number>, id: string | number) => {
    return handledTaskIds.has(id) || handledTaskIds.has(String(id));
};

// 任务中心事件必须按后端推送的任务 id 定位，避免状态更新误覆盖列表第一项。
export const findTaskCenterItemIndex = <T extends { id?: string | number }>(tasks: T[], task: T) => {
    const targetTaskId = normalizeTaskId(task.id);
    return tasks.findIndex(item => normalizeTaskId(item.id) === targetTaskId);
};

// 物流导入任务使用稳定业务字段识别，不能依赖业务名称、任务名称或文件名文案。
export const isLogisticsImportTask = (task: LogisticsImportTaskLike, importTaskType: string) => {
    return task.taskType === importTaskType && task.businessType === LOGISTICS_IMPORT_BUSINESS_TYPE;
};

export const isCompletedLogisticsImportTaskForDocument = (
    task: LogisticsImportTaskLike,
    options: Omit<CompletedLogisticsImportTaskOptions<LogisticsImportTaskLike>, "tasks" | "handledTaskIds">
) => {
    if (!isLogisticsImportTask(task, options.importTaskType)) return false;
    if (task.status !== options.completedStatus) return false;
    if (task.businessId !== options.currentBusinessId) return false;
    if (task.businessScene !== options.currentBusinessScene) return false;
    if (options.pendingTaskId !== undefined && normalizeTaskId(task.id) !== normalizeTaskId(options.pendingTaskId)) return false;
    return true;
};

export const getCompletedLogisticsImportTaskForDocument = <T extends LogisticsImportTaskLike>(
    options: CompletedLogisticsImportTaskOptions<T>
) => {
    return options.tasks.find(task => {
        if (task.id !== undefined && hasHandledTask(options.handledTaskIds, task.id)) return false;
        return isCompletedLogisticsImportTaskForDocument(task, options);
    });
};
